/* Reviews hub: tab switching, client-side pagination of the video grid, and
   the reels lightbox. The lightbox is the Wrist-X UGC player — this element
   extends that class so open/close/navigate/mute/swipe stay one
   implementation. The base builds its item list from autoplaying <video>
   tiles; here the tiles are poster images, so connectedCallback wires the
   same controls against `data-rh-video` buttons and the tile pause/resume
   hooks become no-ops. */
customElements.whenDefined('wristx-ugc-lightbox').then(function () {
  if (customElements.get('reviews-hub')) return;
  var Base = customElements.get('wristx-ugc-lightbox');

  customElements.define(
    'reviews-hub',
    class ReviewsHub extends Base {
      connectedCallback() {
        if (this.rhInitialized) return;
        this.rhInitialized = true;

        this.initTabs();
        this.initPager();
        this.initLightbox();
      }

      /* ── Tabs ─────────────────────────────────────────────────────── */

      initTabs() {
        var self = this;
        this.tabs = Array.from(this.querySelectorAll('[data-rh-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-rh-panel]'));
        if (!this.tabs.length) return;

        this.tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            self.selectTab(tab.getAttribute('data-rh-tab'), true);
          });
          tab.addEventListener('keydown', function (e) {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            e.preventDefault();
            var i = self.tabs.indexOf(tab);
            var n = self.tabs.length;
            var next = self.tabs[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n];
            self.selectTab(next.getAttribute('data-rh-tab'), true);
            next.focus();
          });
        });

        // deep link: /pages/reviews#written or ?tab=written
        var wanted = (location.hash || '').replace('#', '') ||
          new URLSearchParams(location.search).get('tab');
        if (wanted && this.querySelector('[data-rh-tab="' + wanted + '"]')) {
          this.selectTab(wanted, false);
        }
      }

      selectTab(name, updateHash) {
        this.tabs.forEach(function (tab) {
          var on = tab.getAttribute('data-rh-tab') === name;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
        });
        this.panels.forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-rh-panel') !== name;
        });
        // Judge.me's Masonry re-lays out on window resize; nudge it now that
        // the panel is visible so any deferred measurement catches up
        requestAnimationFrame(function () {
          window.dispatchEvent(new Event('resize'));
        });
        if (updateHash && window.history && history.replaceState) {
          history.replaceState(null, '', name === 'video' ? location.pathname + location.search : '#' + name);
        }
      }

      /* ── Pager ────────────────────────────────────────────────────── */

      initPager() {
        var self = this;
        this.grid = this.querySelector('[data-rh-grid]');
        this.pager = this.querySelector('[data-rh-pager]');
        if (!this.grid || !this.pager) return;

        this.tiles = Array.from(this.grid.querySelectorAll('[data-rh-video]'));
        this.perPageMobile = parseInt(this.getAttribute('data-rh-per-page'), 10) || 12;
        this.perPageDesktop = parseInt(this.getAttribute('data-rh-per-page-desktop'), 10) || this.perPageMobile;
        this.page = 0;

        this.prevBtn = this.pager.querySelector('[data-rh-prev]');
        this.nextBtn = this.pager.querySelector('[data-rh-next]');
        this.pageList = this.pager.querySelector('[data-rh-pages]');

        this.prevBtn.addEventListener('click', function () { self.goToPage(self.page - 1, true); });
        this.nextBtn.addEventListener('click', function () { self.goToPage(self.page + 1, true); });

        // 750px is where section-reviews-hub.css swaps the 2-column mobile grid
        // for the wide auto-fill one, so the page size changes with the layout.
        this.wide = window.matchMedia('(min-width: 750px)');
        var onBreakpoint = function () { self.buildPages(); };
        if (this.wide.addEventListener) this.wide.addEventListener('change', onBreakpoint);
        else if (this.wide.addListener) this.wide.addListener(onBreakpoint); // Safari < 14

        this.buildPages();
      }

      /* Builds (or rebuilds) the numbered pager for the current page size and
         keeps the reader on the row they were looking at across a resize. */
      buildPages() {
        var self = this;
        var firstVisible = this.perPage ? this.page * this.perPage : 0;

        this.perPage = this.wide.matches ? this.perPageDesktop : this.perPageMobile;
        this.pageCount = Math.max(1, Math.ceil(this.tiles.length / this.perPage));
        this.pageList.textContent = '';
        this.pageBtns = [];

        if (this.pageCount < 2) {
          this.pager.hidden = true;
          this.page = 0;
          this.tiles.forEach(function (tile) { tile.hidden = false; });
          return;
        }

        for (var i = 0; i < this.pageCount; i++) {
          (function (n) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'rh__pager-num';
            btn.textContent = String(n + 1);
            btn.setAttribute('aria-label', 'Page ' + (n + 1));
            btn.addEventListener('click', function () { self.goToPage(n, true); });
            self.pageList.appendChild(btn);
            self.pageBtns.push(btn);
          })(i);
        }

        this.pager.hidden = false;
        this.goToPage(Math.min(Math.floor(firstVisible / this.perPage), this.pageCount - 1), false);
      }

      goToPage(n, scroll) {
        if (n < 0 || n >= this.pageCount) return;
        this.page = n;
        var start = n * this.perPage;
        var end = start + this.perPage;
        this.tiles.forEach(function (tile, i) {
          tile.hidden = i < start || i >= end;
        });
        this.pageBtns.forEach(function (btn, i) {
          if (i === n) btn.setAttribute('aria-current', 'page');
          else btn.removeAttribute('aria-current');
        });
        this.prevBtn.disabled = n === 0;
        this.nextBtn.disabled = n === this.pageCount - 1;
        if (scroll) this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      /* ── Lightbox (wiring only; behaviour is inherited) ───────────── */

      initLightbox() {
        var self = this;
        this.items = Array.from(this.querySelectorAll('[data-rh-video]')).map(function (tile) {
          return {
            tile: tile,
            src: tile.getAttribute('data-rh-video'),
            poster: tile.getAttribute('data-rh-poster') || ''
          };
        }).filter(function (item) { return item.src; });
        if (!this.items.length) return;

        this.modal = this.querySelector('[data-wxlb-modal]');
        this.frame = this.querySelector('[data-wxlb-frame]');
        this.player = this.querySelector('[data-wxlb-video]');
        this.playerSource = this.querySelector('[data-wxlb-source]');
        this.dotsWrap = this.querySelector('[data-wxlb-dots]');
        this.countEl = this.querySelector('[data-wxlb-count]');
        this.closeBtn = this.querySelector('[data-wxlb-close-btn]');
        this.stateEl = this.querySelector('[data-wxlb-state]');
        this.progressEl = this.querySelector('[data-wxlb-progress]');
        this.muteBtn = this.querySelector('[data-wxlb-mute]');
        if (!this.modal || !this.player || !this.playerSource) return;

        this.currentIndex = 0;
        this.opener = null;
        this.lastSwipeAt = 0;
        this.onKeydown = this.onKeydown.bind(this);

        this.items.forEach(function (item, index) {
          item.tile.addEventListener('click', function () {
            self.open(index, item.tile);
          });
        });

        this.querySelectorAll('[data-wxlb-close], [data-wxlb-close-btn]').forEach(function (el) {
          el.addEventListener('click', function () { self.close(); });
        });

        var prev = this.querySelector('[data-wxlb-prev]');
        var next = this.querySelector('[data-wxlb-next]');
        if (prev) prev.addEventListener('click', function () { self.step(-1); });
        if (next) next.addEventListener('click', function () { self.step(1); });

        if (this.muteBtn) {
          this.muteBtn.addEventListener('click', function () {
            self.setMuted(!self.player.muted);
          });
        }

        this.frame.addEventListener('click', function (e) {
          if (e.target.closest('button')) return;
          if (Date.now() - self.lastSwipeAt < 500) return;
          self.togglePlay();
        });

        this.player.addEventListener('play', function () {
          if (self.stateEl) self.stateEl.hidden = true;
        });
        this.player.addEventListener('pause', function () {
          if (self.stateEl && !self.modal.hidden) self.stateEl.hidden = false;
        });
        this.player.addEventListener('timeupdate', function () {
          if (self.progressEl && self.player.duration > 0) {
            self.progressEl.style.transform =
              'scaleX(' + self.player.currentTime / self.player.duration + ')';
          }
        });

        this.buildDots();
        if (this.items.length < 2) {
          if (prev) prev.hidden = true;
          if (next) next.hidden = true;
          if (this.dotsWrap) this.dotsWrap.hidden = true;
          if (this.countEl) this.countEl.hidden = true;
        }

        this.bindSwipe();
      }

      // a reviews page can hold dozens of clips: the "3 / 14" counter does
      // the job, a row of dots would overflow the frame
      buildDots() {
        this.dots = [];
        if (this.dotsWrap) this.dotsWrap.hidden = true;
      }

      // the grid holds posters, not autoplaying clips: nothing to pause
      pauseTiles() {}
      resumeTiles() {}
    }
  );
});
